'use client';

import { useEffect, useRef, useState } from 'react';
import { Box } from 'lucide-react';

export interface ModelViewerProps {
  src: string;
  alt: string;
  poster?: string;
  caption?: string;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'failed';

const SCRIPT_SRC = '/vendor/model-viewer/model-viewer.min.js';
let scriptPromise: Promise<void> | null = null;

/**
 * Injects the self-hosted <model-viewer> bundle once per page. It is a plain
 * <script type="module"> rather than an import() because the library carries
 * a bare dynamic import for an optional Lottie loader that Turbopack refuses
 * to compile. See public/vendor/model-viewer/README.md.
 */
const loadModelViewerScript = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.customElements?.get('model-viewer')) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('model-viewer failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

/**
 * Interactive 3D viewer built on Google's <model-viewer> web component.
 *
 * The ~1 MB bundle is not part of any page chunk: it is fetched only once this
 * component is mounted AND scrolled to within 400px of the viewport. Pages
 * without a 3D block never pay for it, and pages with one pay only when the
 * reader gets there. Until then the poster (or a placeholder) fills the frame.
 */
export const ModelViewer = ({ src, alt, poster, caption }: ModelViewerProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<LoadState>('idle');

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    let cancelled = false;

    const load = () => {
      setState('loading');
      loadModelViewerScript()
        .then(() => {
          if (!cancelled) setState('ready');
        })
        .catch(() => {
          if (!cancelled) setState('failed');
        });
    };

    if (typeof IntersectionObserver === 'undefined') {
      load();
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  return (
    <figure className="my-8">
      <div
        ref={frameRef}
        className="relative aspect-[3/2] w-full overflow-hidden rounded-lg border border-zinc-700 bg-[#141414]"
      >
        {state === 'ready' ? (
          <model-viewer
            src={src}
            alt={alt}
            poster={poster}
            camera-controls
            shadow-intensity="0.6"
            exposure="1.1"
            camera-orbit="-30deg 60deg 85%"
            min-camera-orbit="auto 0deg 30%"
            max-camera-orbit="auto 180deg 150%"
            touch-action="pan-y"
            interaction-prompt="auto"
            style={{ width: '100%', height: '100%', backgroundColor: '#141414' }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {poster ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={poster} alt={alt} className="h-full w-full object-contain" />
            ) : (
              <Box className="h-10 w-10 text-zinc-600" aria-hidden />
            )}
            {state === 'failed' && (
              <div className="absolute inset-x-0 bottom-0 bg-black/70 px-4 py-2 text-center text-sm text-zinc-300">
                3D viewer failed to load. {alt}
              </div>
            )}
          </div>
        )}
        {state === 'ready' && (
          <div className="pointer-events-none absolute bottom-2 right-3 text-xs text-zinc-500">
            drag to rotate · scroll to zoom
          </div>
        )}
      </div>
      {caption && <figcaption className="mt-3 text-sm text-zinc-400">{caption}</figcaption>}
    </figure>
  );
};

export default ModelViewer;
